import React, { useEffect, useState, useMemo } from 'react';
import ButtonIcon from '../../assets/images/icons/button-icon.svg';
import StreamIcon from '../../assets/images/icons/stream.svg';
import FanbaseIcon from '../../assets/images/icons/fanbase.svg';
import BestTrack from '../../assets/images/icons/best-track.svg';
import Listeners from '../../assets/images/icons/listeners.svg';
import StarIcon from '../../assets/images/icons/start-icon.svg';
import FollowersIcon from '../../assets/images/icons/followers.svg';
import BusinessIdeaIcon from '../../assets/images/icons/businees-idea.svg';
import SongRecommendIcon from '../../assets/images/icons/song-recommend.svg'
import AreaChart from './charts/AreaChart';
import { useSelector, useDispatch } from "react-redux";
import * as actions from '../../redux/actions/index';
// import { useLocation } from "react-router-dom";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatNumber } from '../../helper/utility';
import { showToast } from '../../helper/ShowToast';
import { useLocation, useNavigate } from 'react-router-dom';
import ChatInput from './AmplitudeAI/Chat/ChatInput';
import { PricingPlanModal } from '../../components/PricingPlanModal';
import { CreditPlanModal } from '../../components/CreditPlanModal';
import DummyChart from '../../components/DummyChart';
import AppTour from '../../components/AppTour/AppTour';
import {
    canUseAmplitudeChat,
    canViewRealAnalytics,
    getUserDisplayName,
    shouldFetchArtistAnalytics,
} from '../../helper/access';
import { dashboardTourSteps } from '../../components/AppTour/ModuleTourSteps';

export const Dashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const {hash} = useLocation()
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [showCreditPlanModal, setShowCreditPlanModal] = React.useState(false);
    const [runTour, setRunTour] = useState(false)
    const artistDetails = useSelector(state => state.artistDetails);
    const { showLoad } = useSelector((state) => state?.amplitudeReducer);
    const [isLoading, setIsLoading] = useState(false)
    const { artistInfo, artistStats, artistChartStats } = artistDetails;
    const [intervalType, setIntervalType] = useState('all');
    const { hasActiveSubscription, creditDetails } = useSelector((state) => state.billing);
    const access = useSelector((state) => state.auth.access);
    const userDetails = useSelector((state) => state.auth.userDetails);
    const canFetchArtistAnalytics = shouldFetchArtistAnalytics(access, userDetails);
    const activeArtistProfileId = access?.artist?.activeArtistProfileId || null;
    const isLinkArtistLimitedState =
        access?.accessMode === "limited" &&
        ["no_profile", "low_data"].includes(access?.limitedReason);

    const displayName = useMemo(
        () => getUserDisplayName(userDetails, artistInfo, access),
        [userDetails, artistInfo, access]
    );
    const showRealAnalytics = canFetchArtistAnalytics || (!access && hasActiveSubscription);
    const shouldShowAnalyticsSkeleton = showRealAnalytics && isLoading;

    const renderAnalyticsSkeleton = () => (
        <div className="animate-pulse">
            <div className="h-6 w-6 rounded bg-slate-200 mb-2" />
            <div className="h-24 rounded bg-slate-200 mb-3" />
            <div className="h-3 w-20 rounded bg-slate-200 mb-2" />
            <div className="h-3 w-24 rounded bg-slate-200" />
        </div>
    );

    useEffect(() => {
        if (!canFetchArtistAnalytics) return;

        const queryParams = { type: 'all' };
        dispatch(actions.getArtistStats(toast, queryParams, setIsLoading));
        dispatch(actions.getArtistChartStats(toast, queryParams, setIsLoading));
    }, [dispatch, canFetchArtistAnalytics, activeArtistProfileId]);
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const status = params.get("status");
        setTimeout(() => {
            if (status === "success") showToast('success', "Success! Your subscription has been activated.")
            if (status === "cancel") showToast('error', "Payment cancelled")
        }, 100);
        // Remove status from URL WITHOUT reloading
        params.delete("status");
        const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
        window.history.replaceState({}, "", newUrl);
    }, []);

    // Handle hash changes for tour
    useEffect(() => {
        if (hash === "#start-tour") {
            setRunTour(true);
        }
    }, [hash]);

    const handleTypeSelect = (value) => {
        if (!canFetchArtistAnalytics) {
            if (isLinkArtistLimitedState) {
                showToast("info", "Link your artist profile to view performance data.");
                navigate("/link-accounts");
                return;
            }
            showToast("info", "Upgrade to view performance data.");
            setShowPricingPlanModal(true);
            return;
        }
        const queryParams = { type: value };
        dispatch(actions.getArtistStats(toast, queryParams, setIsLoading));
        dispatch(actions.getArtistChartStats(toast, queryParams, setIsLoading));
        setIntervalType(value);
    };

    const streamChart = useMemo(() => ({
        name: 'Total Streams',
        categories: artistChartStats?.totalStreams?.map(entry => entry.date) || [],
        data: artistChartStats?.totalStreams?.map(entry => entry.streams_total) || [],
        intervalType: intervalType
    }), [artistChartStats, intervalType]);

    const followersChart = useMemo(() => ({
        name: 'Total Followers',
        categories: artistChartStats?.totalFollowers?.map(entry => entry.date) || [],
        data: artistChartStats?.totalFollowers?.map(entry => entry.followers_total) || [],
        intervalType: intervalType
    }), [artistChartStats, intervalType]);

    const listenersChart = useMemo(() => ({
        name: 'Total listeners',
        categories: artistChartStats?.totalListeners?.map(entry => entry.date) || [],
        data: artistChartStats?.totalListeners?.map(entry => entry.monthly_listeners_current) || [],
        intervalType: intervalType
    }), [artistChartStats, intervalType]);

    const fanbaseChart = useMemo(() => ({
        name: 'Total Fanbase',
        categories: artistChartStats?.totalFanbase?.map(entry => entry.date) || [],
        data: artistChartStats?.totalFanbase?.map(entry => entry.fanbase_total) || [],
        intervalType: intervalType
    }), [artistChartStats, intervalType]);

    const navigateToAnalytics = () => {
        if (!canFetchArtistAnalytics) {
            if (isLinkArtistLimitedState) {
                showToast("info", "Link your artist profile to unlock analytics.");
                navigate("/link-accounts");
                return;
            }
            showToast("info", "Upgrade to unlock analytics and detailed performance data.");
            setShowPricingPlanModal(true);
            return;
        }
        navigate('/admin/data-analyst')
    }

    const handleNavigations = (actions) => {
        if (actions === 'campaign') {
            navigate('/admin/growth-engine/campaign')
        }
        if (actions === 'tasks') {
            navigate('/admin/artist-manager/release-and-financial-planner')
        }
        if (actions === 'music_link') {
            navigate('/admin/artist-manager/build-music-link')
        }
        // if (actions === 'bio_link') {
        //     navigate('/admin/artist-manager/build-bio-link')
        // }
        if (actions === 'master_track') {
            navigate('/admin/audio-engineer')
        }
        if (actions === 'fan_data') {
            navigate('/admin/data-analyst/catalog')
        }
        if (actions === 'listeners') {
            navigate('/admin/data-analyst')
        }
        if (actions === 'estimate_revenue') {
            navigate('/admin/artist-manager/release-and-financial-planner', {
                state: { scrollToCenter: true }
            })
        }
    }

    const handleSend = (input) => {
        if (access && !canUseAmplitudeChat(access)) {
            showToast("info", "Amplitude AI chat is not available for your account.");
            return;
        }
        if (!creditDetails?.data || creditDetails?.data?.remaining <= 0) {
            showToast("info", "You are out of credits. Please purchase more to continue.");
            setShowCreditPlanModal(true);
            return;
        }
        dispatch(actions.sendMessage(toast, input));
        navigate("/admin/amplitude-ai");
    };

    const handleProClick = async () => {
        try {
            setIsLoading(true); // 🔹 start loading
            if (hasActiveSubscription) {
                await dispatch(actions.getCustomerPortalSession());
            } else {
                setShowPricingPlanModal(true);
            }
        } catch (err) {
            console.error('Error handling Pro button:', err);
        } finally {
            setIsLoading(false); // 🔹 stop loading
        }
    };

    const handleAnalyticsUnlock = () => {
        if (isLinkArtistLimitedState) {
            navigate("/link-accounts");
            return;
        }
        handleProClick();
    };

    return (
        <div className='dashboard-page mb-5'>
            <div className="container max-w-full">
                <AppTour
                    tourSteps={dashboardTourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                />
                <div className='mt-4 mb-4' data-tour="amplitude-ai-chat">
                    <ChatInput
                        title={`You’re in control of your journey, ${displayName}.`}
                        inputPlaceholder={'Ask your AI Assistant, Amplitude for help.'}
                        onSend={handleSend}
                        disabled={showLoad}
                    />
                </div>

                {/* Performance Section */}
                <div className="performance-section relative bg-theme_clr1 rounded-xl mb-5 p-4 lg:px-5 pt-[26px] pb-[30px]" data-tour="performance-section">
                    <div className="header-section flex flex-wrap justify-between gap-2 mb-[10px]">
                        <select name="" id="" className='appearance-none bg-theme_clr1 text-white text-base font-manrope border-0 focus:outline-none pr-8'
                            onChange={(e) => handleTypeSelect(e.target.value)}>
                            <option value="all">All time performance</option>
                            <option value="weekly">Weekly performance</option>
                            <option value="monthly">Monthly performance</option>
                            <option value="yearly">Yearly performance</option>
                        </select>
                        <button type='button' onClick={navigateToAnalytics} className='btn-custom3'>View Full Analytics</button>
                    </div>
                    <div className={`grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 2xl:gap-[22px] flex-nowrap overflow-auto ${showRealAnalytics ? "" : "opacity-70 blur-[1px]"}`}>
                        <div className="grid-item">
                            <div className="info-box h-full bg-white font-manrope flex flex-col justify-between gap-2 rounded-xl p-4 2xl:p-5">
                                {shouldShowAnalyticsSkeleton ? (
                                    renderAnalyticsSkeleton()
                                ) : showRealAnalytics ?
                                    <>
                                        <div className="icon mb-1">
                                            <img src={StreamIcon} alt="Streams" />
                                        </div>
                                        <div className="chart-wrapper" style={{ marginLeft: '-10px' }}>
                                            {streamChart.categories.length > 0 ? <AreaChart key={intervalType} state={streamChart} /> : null}
                                        </div>
                                        <div className="cover">
                                            <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">{formatNumber(artistStats?.totalStreams || 0)}</div>
                                            <div className="text-theme_clr1 text-[12.8px]">Streams + Views</div>
                                        </div>
                                    </> :
                                    <DummyChart 
                                        icon={StreamIcon}
                                        chartType="area"
                                        topText="3M"
                                        bottomText="Streams + Views"
                                        showCta={false}
                                    />
                                }
                            </div>
                        </div>
                        <div className="grid-item">
                            <div className="info-box h-full bg-white font-manrope flex flex-col justify-between gap-2 rounded-xl p-4 2xl:p-5">
                                {shouldShowAnalyticsSkeleton ? (
                                    renderAnalyticsSkeleton()
                                ) : showRealAnalytics ?
                                    <>
                                        <div className="icon mb-1">
                                            <img src={FanbaseIcon} alt="Fanbase" />
                                        </div>
                                        <div className="chart-wrapper" style={{ marginLeft: '-10px' }}>
                                            {/* <AreaChart /> */}
                                            {fanbaseChart.categories.length > 0 ? <AreaChart key={intervalType} state={fanbaseChart} /> : null}
                                        </div>
                                        <div className="cover">
                                            <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">{formatNumber(artistStats?.totalFanbase || 0)}</div>
                                            <div className="text-theme_clr1 text-[12.8px]">{intervalType === 'weekly' ? 'Current ' : ''}Fanbase</div>
                                        </div>
                                    </> :
                                    <DummyChart 
                                        icon={FanbaseIcon}
                                        chartType="area"
                                        topText="223k"
                                        bottomText="Fanbase"
                                        showCta={false}
                                    />
                                }
                            </div>
                        </div>
                        <div className="grid-item">
                            <div className="info-box h-full bg-white font-manrope flex flex-col justify-between gap-2 rounded-xl p-4 2xl:p-5">
                                {shouldShowAnalyticsSkeleton ? (
                                    renderAnalyticsSkeleton()
                                ) : showRealAnalytics ?
                                    <>
                                        <div className="icon">
                                            <img src={BestTrack} alt="Best Track" />
                                            <div className="text-black text-[12.26px] font-bold mt-2">{artistStats?.topTrackData?.track_name || ''}</div>
                                        </div>
                                        <div className="chart-wrapper">
                                            <img src={artistStats?.topTrackData?.image_url || ''} alt="Best Track" className='block w-[120px] mx-auto' />
                                        </div>
                                        <div className="cover">
                                            <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">{formatNumber(artistStats?.topTrackData?.streamsCount || 0)}</div>
                                            <div className="text-theme_clr1 text-[12.8px]">Best Performing Track</div>
                                        </div>
                                    </> :
                                    <DummyChart 
                                        icon={BestTrack}
                                        chartType="image"
                                        topText="154k"
                                        bottomText="Best Performing Track"
                                        showCta={false}
                                    />
                                }
                            </div>
                        </div>
                        <div className="grid-item">
                            <div className="info-box h-full bg-white font-manrope flex flex-col justify-between gap-2 rounded-xl p-4 2xl:p-5">
                                {shouldShowAnalyticsSkeleton ? (
                                    renderAnalyticsSkeleton()
                                ) : showRealAnalytics ?
                                    <>
                                        <div className="icon mb-1">
                                            <img src={Listeners} alt="Listeners" />
                                        </div>
                                        <div className="chart-wrapper" style={{ marginLeft: '-10px' }}>
                                            {listenersChart.categories.length > 0 ? <AreaChart key={intervalType} state={listenersChart} /> : null}
                                        </div>
                                        <div className="cover">
                                            <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">{formatNumber(artistStats?.totalListeners || 0)}</div>
                                            <div className="text-theme_clr1 text-[12.8px]">{intervalType === 'weekly' ? 'Current ' : ''}Listeners</div>
                                        </div>
                                    </> :
                                    <DummyChart 
                                        icon={Listeners}
                                        chartType="area"
                                        topText="254k"
                                        bottomText="Listeners"
                                        showCta={false}
                                    />
                                }
                            </div>
                        </div>
                        <div className="grid-item sm:col-span-2 xl:col-span-1">
                            <div className="info-box h-full bg-white font-manrope flex flex-col justify-between gap-2 rounded-xl p-4 2xl:p-5">
                                {shouldShowAnalyticsSkeleton ? (
                                    renderAnalyticsSkeleton()
                                ) : showRealAnalytics ?
                                    <>
                                        <div className="icon mb-1">
                                            <img src={FollowersIcon} alt="FollowersIcon" />
                                        </div>
                                        <div className="chart-wrapper" style={{ marginLeft: '-10px' }}>
                                            {followersChart.categories.length > 0 ? <AreaChart key={intervalType} state={followersChart} /> : null}
                                        </div>
                                        <div className="cover">
                                            <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">{formatNumber(artistStats?.totalFollowers || 0)}</div>
                                            <div className="text-theme_clr1 text-[12.8px]">Followers</div>
                                        </div>
                                    </> : 
                                    <DummyChart 
                                        icon={FollowersIcon}
                                        chartType="area"
                                        topText="745k"
                                        bottomText="Best Performing Track"
                                        showCta={false}
                                    />
                                }
                            </div>
                        </div>
                    </div>
                    {!showRealAnalytics && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-black/45 px-4">
                            <div className="w-full max-w-md rounded-xl bg-white p-5 text-center">
                                <h3 className="text-theme_clr1 text-lg font-bold mb-2">
                                    Unlock Full Analytics
                                </h3>
                                <p className="text-sm text-slate-600 mb-4">
                                    {isLinkArtistLimitedState
                                        ? "Link your artist profile to unlock analytics across streams, fanbase, listeners, and followers."
                                        : "Upgrade your plan to unlock full analytics across streams, fanbase, listeners, and followers."}
                                </p>
                                <button
                                    type="button"
                                    onClick={handleAnalyticsUnlock}
                                    className="btn-custom3 !py-2 !px-5"
                                >
                                    {isLinkArtistLimitedState ? "Link Your Artist" : "Go Pro"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Grid Listing Section */}
                <div className="performance-section">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                        <div className="grid-item" data-tour="finance-section">
                            <div className="info-box h-full bg-theme_clr1 text-white font-manrope flex flex-col justify-between rounded-xl p-4 xl:p-5">
                                <div className="title-info mb-3">
                                    <div className="text-[21.56px] font-bold mb-4">Understand Your Finances</div>
                                    <div className="text-[12.5px]">Track earnings, expenses, and forecasts — and project your upcoming revenue with confidence.</div>
                                </div>
                                <div className="icon text-center mb-3">
                                    <img src={BusinessIdeaIcon} alt="ProfessorU" className='block max-w-[100px] mx-auto' />
                                </div>
                                <div className="btn-wrapper">
                                    <button type='button' onClick={() => { handleNavigations('tasks') }} className='text-white text-xs font-bold bg-gradient-to-b from-theme_clr2 from-[73%] to-[#FFB700] rounded-lg leading-[22.4px] px-4 py-2'>Get Started</button>
                                </div>
                            </div>
                        </div>
                        <div className="grid-item" data-tour="music-link">
                            <div className="info-box h-full bg-theme_clr1 text-white font-manrope flex flex-col justify-between rounded-xl p-4 xl:p-5">
                                <div className="title-info mb-3">
                                    <div className="text-[21.56px] font-bold mb-4">Create Your Music Link</div>
                                    <div className="text-[12.5px]">Build a beautiful landing page to share your music in seconds.</div>
                                </div>
                                <div className="icon text-center mb-3">
                                    <img src={require('../../assets/images/icons/Fanlink.png')} alt="Fanlink" className='block max-w-[166px] mx-auto' />
                                </div>
                                <div className="btn-wrapper">
                                    <button type='button' onClick={() => { handleNavigations('music_link') }} className='text-white text-xs font-bold bg-gradient-to-b from-theme_clr2 from-[73%] to-[#FFB700] rounded-lg leading-[22.4px] px-4 py-2'>Get Started</button>
                                </div>
                            </div>
                        </div>
                        <div className="grid-item" data-tour="build-campaign">
                            <div className="info-box h-full bg-theme_clr1 text-white font-manrope flex flex-col justify-between rounded-xl p-4 xl:p-5">
                                <div className="title-info mb-3">
                                    <div className="text-[21.56px] font-bold mb-4">Build Your Campaign</div>
                                    <div className="text-[12.5px]">Launch hyper-targeted ad campaigns to grow your fanbase faster.</div>
                                </div>
                                <div className="icon text-center mb-3">
                                    <img src={require('../../assets/images/icons/Press-Kit.png')} alt="Press-Kit" className='block max-w-[72px] mx-auto' />
                                </div>
                                <div className="btn-wrapper">
                                    <button type='button' onClick={() => { handleNavigations('campaign') }} className='text-white text-xs font-bold bg-gradient-to-b from-theme_clr2 from-[73%] to-[#FFB700] rounded-lg leading-[22.4px] px-4 py-2'>Get Started</button>
                                </div>
                            </div>
                        </div>
                        <div className="grid-item" data-tour="master-track">
                            <div className="info-box h-full bg-theme_clr1 text-white font-manrope flex flex-col justify-between rounded-xl p-4 xl:p-5">
                                <div className="title-info mb-3">
                                    <div className="text-[21.56px] font-bold mb-4">Master Your Track </div>
                                    <div className="text-[12.5px]">Get release-ready sound with professional mastering — without the high cost.</div>
                                </div>
                                <div className="icon text-center mb-3">
                                    <img src={SongRecommendIcon} alt="Exclusives" className='block max-w-[100px] mx-auto' />
                                </div>
                                <div className="btn-wrapper">
                                    <button type='button' onClick={() => { handleNavigations('master_track') }} className='text-white text-xs font-bold bg-gradient-to-b from-theme_clr2 from-[73%] to-[#FFB700] rounded-lg leading-[22.4px] px-4 py-2'>Get Started</button>
                                </div>
                            </div>
                        </div>
                        <div className="grid-item" data-tour="data-analyst">
                            <div className="info-box h-full bg-theme_clr1 text-white font-manrope flex flex-col justify-between rounded-xl p-4 xl:p-5">
                                <div className="title-info mb-3">
                                    <div className="text-[21.56px] font-bold mb-4">Geographic Data</div>
                                    <div className="text-[12.5px]">Understand where your audience is — and engage them more effectively.</div>
                                </div>
                                <div className="icon text-center mb-3">
                                    <img src={require('../../assets/images/icons/Fan-Data.png')} alt="Fan Data" className='block max-w-[70px] mx-auto' />
                                </div>
                                <div className="btn-wrapper">
                                    <button type='button' onClick={() => { handleNavigations('fan_data') }} className='text-white text-xs font-bold bg-gradient-to-b from-theme_clr2 from-[73%] to-[#FFB700] rounded-lg leading-[22.4px] px-4 py-2'>Get Started</button>
                                </div>
                            </div>
                        </div>
                        <div className="grid-item" data-tour="release-planner">
                            <div className="info-box h-full bg-theme_clr1 text-white font-manrope flex flex-col justify-between rounded-xl p-4 xl:p-5">
                                <div className="title-info mb-3">
                                    <div className="text-[21.56px] font-bold mb-4">Release Planner</div>
                                    <div className="text-[12.5px]">Plan and schedule your releases for maximum impact.</div>
                                </div>
                                <div className="icon text-center mb-3">
                                    <img src={require('../../assets/images/icons/Release-Planner.png')} alt="Release Planner" className='block max-w-[65px] mx-auto' />
                                </div>
                                <div className="btn-wrapper">
                                    <button type='button' onClick={() => { handleNavigations('tasks') }} className='text-white text-xs font-bold bg-gradient-to-b from-theme_clr2 from-[73%] to-[#FFB700] rounded-lg leading-[22.4px] px-4 py-2'>Get Started</button>
                                </div>
                            </div>
                        </div>
                        <div className="grid-item order-first xl:order-last sm:col-span-2 lg:col-span-3 xl:row-start-1 xl:row-end-3 xl:col-start-4" data-tour="quick-actions">
                            <div className="info-box h-full bg-theme_clr1 text-white font-manrope rounded-xl p-4 xl:p-5">
                                <div className="text-[21.56px] font-bold mb-4">Quick Actions</div>
                                <div className="btn-wrapper flex flex-wrap xl:flex-col gap-3 xl:gap-4">
                                    <button type='button' onClick={() => handleNavigations('campaign')} className='btn-custom3 !py-3'>Create New Campaign</button>
                                    <button type='button' onClick={() => handleNavigations('tasks')} className='btn-custom3 !py-3'>Plan your Tasks</button>
                                    <button type='button' onClick={() => handleNavigations('listeners')} className='btn-custom3 !py-3'>Understand your Listeners</button>
                                    <button type='button' onClick={() => handleNavigations('music_link')} className='btn-custom3 !py-3'>Create a Music Link </button>
                                    <button type='button' onClick={() => handleNavigations('estimate_revenue')} className='btn-custom3 !py-3'>Estimate Revenue</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/dashboard"} />
            <CreditPlanModal isOpen={showCreditPlanModal} onClose={() => setShowCreditPlanModal(false)} />
        </div>
    )
}
